import type { ViewRef } from "@/api/views";
import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";

export interface WikiDirectoryEntry {
  name: string;
  wiki_path: string;
  summary: string;
}

export interface WikiFileEntry {
  name: string;
  wiki_path: string;
  summary: string;
}

export interface WikiTreeResponse {
  path: string;
  directories: WikiDirectoryEntry[];
  files: WikiFileEntry[];
  can_write: boolean;
}

export interface WikiPageResponse {
  path: string;
  title: string;
  content: string;
  word_count: number;
  links: string[];
  can_write: boolean;
  /** Present only when this page is inside the configured write scope. */
  edit_url: string | null;
}

export interface WikiBacklinksResponse {
  path: string;
  backlinks: string[];
}

export interface CreateWikiPageInput {
  path: string;
  content: string;
}

export interface CreateWikiPageResponse {
  path: string;
  title: string;
  content: string;
  can_write: boolean;
}

export class WikiApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "WikiApiError";
  }
}

function wikiBasePath(viewRef: ViewRef): string {
  if (viewRef.scope === "conversation") {
    return `/api/sessions/${encodeURIComponent(viewRef.sessionId)}/views/${encodeURIComponent(
      viewRef.viewId,
    )}/wiki`;
  }
  return `/api/views/${encodeURIComponent(viewRef.agentId)}/${encodeURIComponent(
    viewRef.viewId,
  )}/wiki`;
}

async function responseError(response: Response): Promise<WikiApiError> {
  let message = `Request failed (${response.status})`;
  try {
    const payload = await response.json();
    const detail = payload?.detail ?? payload?.error ?? payload?.message;
    if (typeof detail === "string") message = detail;
  } catch {
    // A generic status remains useful if an upstream response has no JSON body.
  }
  return new WikiApiError(message, response.status);
}

async function wikiRequest<T>(
  path: string,
  init: RequestInit,
  getToken: AccessTokenGetter,
): Promise<T> {
  const response = await fetchWithAuth(path, init, getToken);
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<T>;
}

export function fetchWikiTree(
  viewRef: ViewRef,
  path: string,
  getToken: AccessTokenGetter,
): Promise<WikiTreeResponse> {
  const params = new URLSearchParams();
  if (path) params.set("path", path);
  const suffix = params.size ? `?${params.toString()}` : "";
  return wikiRequest(
    `${wikiBasePath(viewRef)}/tree${suffix}`,
    { headers: {} },
    getToken,
  );
}

export function fetchWikiPage(
  viewRef: ViewRef,
  path: string,
  getToken: AccessTokenGetter,
): Promise<WikiPageResponse> {
  const params = new URLSearchParams({ path });
  return wikiRequest(
    `${wikiBasePath(viewRef)}/page?${params.toString()}`,
    { headers: {} },
    getToken,
  );
}

export function fetchWikiBacklinks(
  viewRef: ViewRef,
  path: string,
  getToken: AccessTokenGetter,
): Promise<WikiBacklinksResponse> {
  const params = new URLSearchParams({ path });
  return wikiRequest(
    `${wikiBasePath(viewRef)}/backlinks?${params.toString()}`,
    { headers: {} },
    getToken,
  );
}

export function createWikiPage(
  viewRef: ViewRef,
  input: CreateWikiPageInput,
  getToken: AccessTokenGetter,
): Promise<CreateWikiPageResponse> {
  return wikiRequest(
    `${wikiBasePath(viewRef)}/page`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
    getToken,
  );
}

/** Replace one page through the audited conversation-view write boundary. */
export function updateWikiPage(
  viewRef: Extract<ViewRef, { scope: "conversation" }>,
  path: string,
  content: string,
  getToken: AccessTokenGetter,
): Promise<WikiPageResponse> {
  const params = new URLSearchParams({ path });
  return wikiRequest(
    `${wikiBasePath(viewRef)}/page?${params.toString()}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    },
    getToken,
  );
}
